import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Plus, Search, Trash2, Edit2, BookOpen, ChevronDown, ChevronUp, RefreshCw, FileText, AlertCircle, CheckCircle2, Clock, XCircle, ArrowUpDown, ArrowUp, ArrowDown, DollarSign, Layers, X, Filter, RotateCw, MessageSquare, Link2, Video, User } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { PdpMockEditor } from '../../components/PdpMockEditor';
import { toast } from 'sonner';
import { ZohoCustomerLink, ZohoInvoiceLink, ZohoCrmLeadLink, MissingCrmLeadBadge, EnrollmentNoCrmLink, ZohoCrmBatchLink } from '../../components/ZohoLinks';
import { BookmarkInline, BookmarkMapProvider, useBookmarkMap, bookmarkRowClass } from '../../components/BookmarkButton';
import StudentTimeline from '../../components/StudentTimeline';


function MultiSelect({ options, selected, onChange, placeholder, testId, searchable = false }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const toggle = (val) => {
    const s = new Set(selected);
    if (s.has(val)) s.delete(val); else s.add(val);
    onChange(Array.from(s));
  };
  const label = selected.length === 0 ? placeholder : `${selected.length} selected`;
  const filtered = searchable && searchTerm ? options.filter(o => o.label.toLowerCase().includes(searchTerm.toLowerCase())) : options;
  return (
    <div className="relative" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 h-9 px-3 border rounded-sm text-xs text-slate-600 bg-white hover:bg-slate-50 min-w-[120px]">
        <Filter className="w-3 h-3 text-slate-400" />{label}
        {selected.length > 0 && <X className="w-3 h-3 ml-auto text-slate-400 hover:text-red-500" onClick={e => { e.stopPropagation(); onChange([]); }} />}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setSearchTerm(''); }} />
          <div className="absolute z-50 w-56 mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-hidden">
            {searchable && (
              <div className="p-1.5 border-b">
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search..." className="w-full h-7 px-2 text-xs border rounded-sm outline-none focus:border-violet-300" autoFocus data-testid={`${testId}-search`} />
              </div>
            )}
            <div className="overflow-y-auto max-h-52">
              {filtered.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-3">No matches</p>
              ) : filtered.map(o => (
                <label key={o.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-violet-50 cursor-pointer text-xs">
                  <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} className="rounded border-slate-300" />
                  {o.label}
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}


function SortHead({ label, field, sortBy, sortOrder, onSort, align }) {
  const active = sortBy === field;
  const handleClick = () => {
    if (active) onSort(field, sortOrder === 'asc' ? 'desc' : 'asc');
    else onSort(field, 'asc');
  };
  return (
    <TableHead className={`text-[10px] uppercase cursor-pointer hover:bg-slate-50 select-none ${align === 'right' ? 'text-right' : ''}`} onClick={handleClick}>
      <span className="inline-flex items-center gap-0.5">
        {label}
        {active ? (sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 text-violet-600" /> : <ArrowDown className="w-3 h-3 text-violet-600" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
      </span>
    </TableHead>
  );
}

export default function StudentsPage() {
  const [urlParams, setUrlParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(urlParams.get('search') || urlParams.get('focus') || '');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showEnroll, setShowEnroll] = useState(null);
  const [batches, setBatches] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [needsBatch, setNeedsBatch] = useState(false);
  const [writeoffFilter, setWriteoffFilter] = useState(''); // '' = all, 'true' = written off, 'false' = not written off
  const [creditFilter, setCreditFilter] = useState(''); // '' = all, 'true' = has credit, 'false' = no credit
  const [hasDues, setHasDues] = useState('');
  const [noCrm, setNoCrm] = useState(false);
  const [missingMock, setMissingMock] = useState(false);
  const [courseFilter, setCourseFilter] = useState([]);
  const [coursesList, setCoursesList] = useState([]);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  // Map of enrollment_no → lead_id (or null when checked & missing). Loaded once.
  const [crmLeadLinks, setCrmLeadLinks] = useState({});
  const [crmRefreshing, setCrmRefreshing] = useState(false);

  // Load CRM lead-link map (zero-cost — pure DB read on backend) once on mount.
  // Also listen for live 404s so the badge + KPI update without a page reload.
  const reloadCrmLeadLinks = useCallback(() => {
    api.get('/training/crm/lead-links')
      .then(r => setCrmLeadLinks(r.data?.links || {}))
      .catch(() => {});
  }, []);
  useEffect(() => { reloadCrmLeadLinks(); }, [reloadCrmLeadLinks]);
  useEffect(() => {
    const onMissing = (e) => {
      const en = e?.detail?.enrollmentNo;
      if (!en) return;
      // Optimistic local update so the badge appears instantly
      setCrmLeadLinks(prev => ({ ...prev, [String(en)]: null }));
      // Refresh KPI count by re-fetching authoritative state
      reloadCrmLeadLinks();
    };
    window.addEventListener('crm-lead-missing', onMissing);
    return () => window.removeEventListener('crm-lead-missing', onMissing);
  }, [reloadCrmLeadLinks]);

  const fetchStudents = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, page_size: 20 });
    if (search) params.append('search', search);
    if (needsBatch) params.append('needs_batch', 'true');
    if (writeoffFilter) params.append('has_writeoff', writeoffFilter);
    if (creditFilter) params.append('has_credit', creditFilter);
    if (hasDues) params.append('has_dues', hasDues);
    if (noCrm) params.append('no_crm', 'true');
    if (missingMock) params.append('missing_mock_interview', 'true');
    if (courseFilter.length > 0) params.append('course_id', courseFilter.join(','));
    if (sortBy) { params.append('sort_by', sortBy); params.append('sort_order', sortOrder); }
    api.get(`/training/students?${params}`)
      .then(r => { setStudents(r.data.students || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, search, needsBatch, writeoffFilter, creditFilter, hasDues, noCrm, missingMock, courseFilter, sortBy, sortOrder]);

  const fetchSyncStatus = useCallback(() => {
    api.get('/training/zoho/sync-status').then(r => {
      setSyncStatus(r.data.last_sync);
      if (r.data.is_running && !syncing) setSyncing(true);
    }).catch(() => {});
  }, [syncing]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  // Deep-link support: /training/students?focus=<enrollment_no>
  // Notifications and other pages link here so a click lands on the
  // specific student. We seed the search bar with the enrollment_no and
  // auto-expand the student row once it's loaded.
  const focusEnroll = urlParams.get('focus');
  useEffect(() => {
    if (!focusEnroll) return;
    // Seed search so the student appears in the paged list
    if (search !== String(focusEnroll)) {
      setSearch(String(focusEnroll));
      setPage(1);
    }
  }, [focusEnroll, search]);

  useEffect(() => {
    if (!focusEnroll || loading) return;
    if (students.length === 0) {
      // Search executed but no matches — enrolment not in Training Mgmt yet
      toast.warning(`Enrolment ${focusEnroll} not found in Training Mgmt. It may not be synced yet.`);
      const next = new URLSearchParams(urlParams);
      next.delete('focus');
      setUrlParams(next, { replace: true });
      return;
    }
    const target = students.find((s) => String(s.enrollment_no) === String(focusEnroll));
    if (target) {
      setExpandedId(target.id);
      const next = new URLSearchParams(urlParams);
      next.delete('focus');
      setUrlParams(next, { replace: true });
    } else if (students.length > 0) {
      // Fuzzy match brought back other students but not the exact one
      toast.warning(`Exact match for enrolment ${focusEnroll} not found. Showing ${students.length} similar student(s).`);
      const next = new URLSearchParams(urlParams);
      next.delete('focus');
      setUrlParams(next, { replace: true });
    }
  }, [focusEnroll, loading, students, urlParams, setUrlParams]);
  useEffect(() => {
    api.get('/training/courses').then(r => setCoursesList(r.data.courses || []));
  }, []);
  useEffect(() => {
    Promise.all([
      api.get('/training/batches?page_size=200'),
      api.get('/training/counsellors'),
    ]).then(([bR, cR]) => {
      setBatches(bR.data.batches || []);
      setCounsellors(cR.data.counsellors || []);
    });
    fetchSyncStatus();
  }, [fetchSyncStatus]);

  const handleSync = async () => {
    setSyncing(true);
    toast.info('Starting Zoho Books sync...');
    try {
      const r = await api.post('/training/zoho/sync');
      if (r.data.status === 'already_running') {
        toast.warning('A sync is already in progress');
      } else {
        toast.success('Sync started in background. Will refresh when complete.');
        // Poll for completion
        const poll = setInterval(async () => {
          try {
            const sr = await api.get('/training/zoho/sync-status');
            const isRunning = sr.data.is_running;
            setSyncStatus(sr.data.last_sync);
            if (!isRunning) {
              clearInterval(poll);
              setSyncing(false);
              const last = sr.data.last_sync;
              if (last?.status === 'success') {
                toast.success(`Sync complete: ${last.unique_students || 0} students from ${last.filtered_invoices || 0} invoices`);
              } else if (last?.status === 'failed') {
                toast.error(`Sync failed: ${last.error || 'Unknown error'}`);
              }
              fetchStudents();
            }
          } catch { /* ignore poll errors */ }
        }, 3000);
        return;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    }
    setSyncing(false);
  };

  const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '-';

  const [kpi, setKpi] = useState(null);
  const [showMultiBatch, setShowMultiBatch] = useState(false);
  const [multiBatchData, setMultiBatchData] = useState([]);
  const [multiBatchLoading, setMultiBatchLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (needsBatch) params.append('needs_batch', 'true');
    if (writeoffFilter) params.append('has_writeoff', writeoffFilter);
    if (creditFilter) params.append('has_credit', creditFilter);
    if (hasDues) params.append('has_dues', hasDues);
    if (noCrm) params.append('no_crm', 'true');
    if (courseFilter.length > 0) params.append('course_ids', courseFilter.join(','));
    api.get(`/training/reports/kpi/students?${params}`).then(r => setKpi(r.data)).catch(() => {});
  }, [students, search, needsBatch, writeoffFilter, creditFilter, hasDues, noCrm, courseFilter, crmLeadLinks]);

  const loadMultiBatch = () => {
    setShowMultiBatch(true);
    setMultiBatchLoading(true);
    api.get('/training/reports/multi-batch-students')
      .then(r => setMultiBatchData(r.data.rows || []))
      .catch(() => {})
      .finally(() => setMultiBatchLoading(false));
  };

  return (
    <BookmarkMapProvider>
    <div className="space-y-6" data-testid="students-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Students</h1>
          <p className="text-sm text-slate-400 mt-1">{total} students{needsBatch ? ' need batch assignment' : ' total'}</p>
        </div>
        <div className="flex items-center gap-2">
          <SyncStatusBadge status={syncStatus} isRunning={syncing} />
          <Button
            onClick={handleSync}
            disabled={syncing}
            variant="outline"
            className="rounded-sm text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
            data-testid="sync-now-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync from Zoho'}
          </Button>
          <Button onClick={() => setShowAdd(true)} className="bg-violet-600 hover:bg-violet-700 rounded-sm text-xs" data-testid="add-student-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Student
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {kpi && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="student-kpi-cards">
          <Card className="border p-3 text-center"><p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Total</p><p className="text-lg font-bold text-slate-800">{kpi.total_students}</p></Card>
          <Card className="border p-3 text-center"><p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Enrolled</p><p className="text-lg font-bold text-emerald-600">{kpi.enrolled}</p></Card>
          <Card className="border p-3 text-center cursor-pointer hover:border-amber-300" onClick={() => { setNeedsBatch(true); setPage(1); }}><p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">No Batch</p><p className="text-lg font-bold text-amber-600">{kpi.no_batch}</p></Card>
          <Card className="border p-3 text-center cursor-pointer hover:border-purple-300" onClick={loadMultiBatch}><p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Multi Batch</p><p className="text-lg font-bold text-purple-600">{kpi.multi_batch}</p></Card>
          <Card
            className={`border p-3 text-center cursor-pointer transition-colors relative ${noCrm ? 'bg-amber-50 border-amber-400' : 'hover:border-amber-300'}`}
            onClick={() => { setNoCrm(!noCrm); setPage(1); }}
            title="Students whose enrollment number is not set on any CRM Lead"
            data-testid="kpi-no-crm-card"
          >
            <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold flex items-center justify-center gap-1">
              <AlertCircle className="w-2.5 h-2.5 text-amber-500" /> No CRM
            </p>
            <p className="text-lg font-bold text-amber-700">{kpi.no_crm ?? 0}</p>
            <button
              onClick={async (e) => {
                e.stopPropagation();
                if (crmRefreshing) return;
                setCrmRefreshing(true);
                const t = toast.loading('Running CRM check on all students...');
                try {
                  let totalFound = 0, totalMissing = 0, totalLooked = 0, totalNewlyFound = 0;
                  // Loop in case server caps each call (1500 max). Stop when nothing new processed.
                  for (let i = 0; i < 5; i++) {
                    const r = await api.post('/training/crm/lead-links/refresh', null, { params: { max_lookups: 1500 } });
                    totalFound += r.data?.found || 0;
                    totalMissing += r.data?.missing || 0;
                    totalLooked += r.data?.looked_up || 0;
                    totalNewlyFound += r.data?.newly_found || 0;
                    if ((r.data?.looked_up || 0) === 0) break;
                  }
                  await reloadCrmLeadLinks();
                  const newPart = totalNewlyFound > 0 ? ` · ${totalNewlyFound} newly linked` : '';
                  toast.success(`CRM check complete — ${totalLooked} checked, ${totalMissing} missing${newPart}`, { id: t });
                } catch {
                  toast.error('CRM check failed', { id: t });
                } finally {
                  setCrmRefreshing(false);
                }
              }}
              disabled={crmRefreshing}
              className="absolute top-1 right-1 p-1 rounded hover:bg-amber-100 text-amber-600 disabled:opacity-50 transition-colors"
              title="Run CRM check on all students now"
              data-testid="kpi-no-crm-refresh"
            >
              <RotateCw className={`w-3 h-3 ${crmRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </Card>
          <Card className="border p-3 text-center"><p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Overdue</p><p className="text-lg font-bold text-red-600">{kpi.overdue_students}</p></Card>
          <Card className="border p-3 text-center cursor-pointer hover:border-orange-300" onClick={() => window.location.href = '/training/anomalies'}><p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Anomalies</p><p className="text-lg font-bold text-orange-600">{kpi.anomaly_count}</p></Card>
        </div>
      )}

      {/* Money KPI strip — finance snapshot for active filter set */}
      {kpi && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="student-money-kpi">
          <Card className="border p-3">
            <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Total Revenue (paid)</p>
            <p className="text-base font-semibold text-emerald-700 mt-0.5">{fmt(kpi.total_revenue || 0)}</p>
          </Card>
          <Card className={`border p-3 ${creditFilter === 'true' ? 'border-violet-300 bg-violet-50/40' : ''}`}>
            <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Credit Applied</p>
            <p className="text-base font-semibold text-violet-700 mt-0.5">{fmt(kpi.total_credit || 0)}</p>
            <p className="text-[9px] text-slate-400 mt-0.5">across {kpi.with_credit || 0} students</p>
          </Card>
          <Card className={`border p-3 ${writeoffFilter === 'true' ? 'border-rose-300 bg-rose-50/40' : ''}`}>
            <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Write-off</p>
            <p className="text-base font-semibold text-rose-700 mt-0.5">{fmt(kpi.total_writeoff || 0)}</p>
            <p className="text-[9px] text-slate-400 mt-0.5">across {kpi.with_writeoff || 0} students</p>
          </Card>
          <Card className={`border p-3 ${hasDues === 'true' ? 'border-orange-300 bg-orange-50/40' : ''}`}>
            <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Net Outstanding</p>
            <p className="text-base font-semibold text-orange-700 mt-0.5">{fmt(kpi.net_outstanding || 0)}</p>
            <p className="text-[9px] text-slate-400 mt-0.5">{kpi.with_dues || 0} students with dues</p>
          </Card>
          <Card className="border p-3">
            <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Total Pending (Zoho)</p>
            <p className="text-base font-semibold text-slate-700 mt-0.5">{fmt(kpi.total_pending || 0)}</p>
            <p className="text-[9px] text-slate-400 mt-0.5">raw due_amount sum</p>
          </Card>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by name, phone, email, enrollment no..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9 text-xs rounded-sm" data-testid="student-search" />
        </div>
        <Button
          variant={needsBatch ? 'default' : 'outline'}
          size="sm"
          className={`rounded-sm text-xs ${needsBatch ? 'bg-amber-600 hover:bg-amber-700' : 'border-amber-200 text-amber-700 hover:bg-amber-50'}`}
          onClick={() => { setNeedsBatch(!needsBatch); setWriteoffFilter(''); setPage(1); }}
          data-testid="needs-batch-filter"
        >
          <AlertCircle className="w-3.5 h-3.5 mr-1" />
          Needs Batch
        </Button>
        {/* Write-off sub-filter: always visible but contextual when Needs Batch is active */}
        <div className="flex gap-0.5 border rounded-sm overflow-hidden" data-testid="writeoff-filter-group">
          {[
            { val: '', label: 'All', icon: null },
            { val: 'true', label: 'Written Off', color: 'text-red-600' },
            { val: 'false', label: 'Not Written Off', color: 'text-emerald-600' },
          ].map(f => (
            <button key={f.val} onClick={() => { setWriteoffFilter(writeoffFilter === f.val ? '' : f.val); setPage(1); }}
              className={`px-2 py-1.5 text-[10px] font-medium transition-colors ${writeoffFilter === f.val ? (f.val === 'true' ? 'bg-red-50 text-red-700' : f.val === 'false' ? 'bg-emerald-50 text-emerald-700' : 'bg-violet-50 text-violet-700') : 'text-slate-400 hover:bg-slate-50'}`}
              data-testid={`writeoff-${f.val || 'all'}`}
            >{f.label}</button>
          ))}
        </div>
        {/* Credit-note sub-filter */}
        <div className="flex gap-0.5 border rounded-sm overflow-hidden" data-testid="credit-filter-group">
          {[
            { val: '',      label: 'CN: All' },
            { val: 'true',  label: 'Has Credit' },
            { val: 'false', label: 'No Credit' },
          ].map(f => (
            <button key={f.val} onClick={() => { setCreditFilter(creditFilter === f.val ? '' : f.val); setPage(1); }}
              className={`px-2 py-1.5 text-[10px] font-medium transition-colors ${creditFilter === f.val ? (f.val === 'true' ? 'bg-violet-50 text-violet-700' : f.val === 'false' ? 'bg-slate-100 text-slate-600' : 'bg-violet-50 text-violet-700') : 'text-slate-400 hover:bg-slate-50'}`}
              data-testid={`credit-${f.val || 'all'}`}
            >{f.label}</button>
          ))}
        </div>
        {/* No-CRM filter chip */}
        <Button
          variant={noCrm ? 'default' : 'outline'}
          size="sm"
          className={`rounded-sm text-xs ${noCrm ? 'bg-amber-600 hover:bg-amber-700' : 'border-amber-200 text-amber-700 hover:bg-amber-50'}`}
          onClick={() => { setNoCrm(!noCrm); setPage(1); }}
          data-testid="no-crm-filter"
          title="Show only students whose enrollment number is not set on any CRM Lead"
        >
          <AlertCircle className="w-3.5 h-3.5 mr-1" />
          No CRM
        </Button>
        <Button
          variant={hasDues === 'true' ? 'default' : 'outline'}
          size="sm"
          className={`rounded-sm text-xs ${hasDues === 'true' ? 'bg-orange-600 hover:bg-orange-700' : 'border-orange-200 text-orange-700 hover:bg-orange-50'}`}
          onClick={() => { setHasDues(hasDues === 'true' ? '' : 'true'); setPage(1); }}
          data-testid="has-dues-filter"
        >
          <DollarSign className="w-3.5 h-3.5 mr-1" />
          Has Dues
        </Button>
        <Button
          variant="outline" size="sm"
          className="rounded-sm text-xs border-purple-200 text-purple-700 hover:bg-purple-50"
          onClick={loadMultiBatch}
          data-testid="multi-batch-btn"
        >
          <Layers className="w-3.5 h-3.5 mr-1" />
          Multi Batch ({kpi?.multi_batch || 0})
        </Button>
        <MultiSelect
          options={coursesList.map(c => ({ value: c.id, label: c.name }))}
          selected={courseFilter}
          onChange={v => { setCourseFilter(v); setPage(1); }}
          placeholder="Course"
          testId="student-course-filter"
          searchable
        />
        <Button
          variant={missingMock ? 'default' : 'outline'}
          size="sm"
          className={`rounded-sm text-xs ${missingMock ? 'bg-pink-600 hover:bg-pink-700' : 'border-pink-200 text-pink-700 hover:bg-pink-50'}`}
          onClick={() => { setMissingMock(!missingMock); setPage(1); }}
          data-testid="missing-mock-filter"
          title="Show students where at least one enrolled batch has no Mock Interview Link"
        >
          <MessageSquare className="w-3.5 h-3.5 mr-1" />
          Missing Mock
        </Button>
        {(search || needsBatch || writeoffFilter || creditFilter || hasDues || missingMock || courseFilter.length > 0 || sortBy) && (
          <Button variant="ghost" size="sm" className="h-9 px-3 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-sm" onClick={() => { setSearch(''); setNeedsBatch(false); setWriteoffFilter(''); setCreditFilter(''); setHasDues(''); setMissingMock(false); setCourseFilter([]); setSortBy(''); setSortOrder('desc'); setPage(1); }} data-testid="clear-all-filters">
            <X className="w-3.5 h-3.5 mr-1" />Clear Filters
          </Button>
        )}
      </div>

      {/* Multi-Batch Students Dialog */}
      <Dialog open={showMultiBatch} onOpenChange={setShowMultiBatch}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-base">Students with Multiple Batches ({multiBatchData.length})</DialogTitle></DialogHeader>
          {multiBatchLoading ? (
            <div className="h-32 flex items-center justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500" /></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-[10px] uppercase">Enroll No</TableHead>
                <TableHead className="text-[10px] uppercase">Name</TableHead>
                <TableHead className="text-[10px] uppercase">Phone</TableHead>
                <TableHead className="text-[10px] uppercase"># Batches</TableHead>
                <TableHead className="text-[10px] uppercase">Batches</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {multiBatchData.map(r => (
                  <TableRow key={r.student_id}>
                    <TableCell className="text-xs"><ZohoCustomerLink enrollmentNo={r.enrollment_no} zohoCustomerId={r.zoho_customer_id} /></TableCell>
                    <TableCell className="text-sm font-medium">{r.name}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.phone || '-'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[9px] bg-purple-50 text-purple-700 border-purple-200">{r.batch_count}</Badge></TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        {r.batches.map((b, i) => (
                          <span key={i} className="text-[10px] text-slate-500">#{b.batch_number} {b.batch_name} <span className={`font-medium ${b.batch_status === 'Running' ? 'text-emerald-600' : 'text-slate-400'}`}>({b.batch_status})</span></span>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      <Card className="border p-0 overflow-x-auto">
        {loading ? (
          <div className="h-48 flex items-center justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500" /></div>
        ) : students.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-12">{needsBatch ? 'All students have been assigned to batches' : 'No students found'}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] uppercase w-8"></TableHead>
                <SortHead label="Enrollment No" field="enrollment_no" sortBy={sortBy} sortOrder={sortOrder} onSort={(f,o) => { setSortBy(f); setSortOrder(o); setPage(1); }} />
                <SortHead label="Name" field="name" sortBy={sortBy} sortOrder={sortOrder} onSort={(f,o) => { setSortBy(f); setSortOrder(o); setPage(1); }} />
                <TableHead className="text-[10px] uppercase">Phone</TableHead>
                <TableHead className="text-[10px] uppercase">Course</TableHead>
                <SortHead label="Total" field="total_amount" sortBy={sortBy} sortOrder={sortOrder} onSort={(f,o) => { setSortBy(f); setSortOrder(o); setPage(1); }} align="right" />
                <SortHead label="Paid" field="paid_amount" sortBy={sortBy} sortOrder={sortOrder} onSort={(f,o) => { setSortBy(f); setSortOrder(o); setPage(1); }} align="right" />
                <SortHead label="Write Off" field="write_off_amount" sortBy={sortBy} sortOrder={sortOrder} onSort={(f,o) => { setSortBy(f); setSortOrder(o); setPage(1); }} align="right" />
                <SortHead label="Credit" field="credit_amount" sortBy={sortBy} sortOrder={sortOrder} onSort={(f,o) => { setSortBy(f); setSortOrder(o); setPage(1); }} align="right" />
                <SortHead label="Due" field="due_amount" sortBy={sortBy} sortOrder={sortOrder} onSort={(f,o) => { setSortBy(f); setSortOrder(o); setPage(1); }} align="right" />
                <TableHead className="text-[10px] uppercase">Expected Payment</TableHead>
                <TableHead className="text-[10px] uppercase text-center">Invoices</TableHead>
                <TableHead className="text-[10px] uppercase">Batches</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {students.map(s => (
                <StudentRow
                  key={s.id}
                  student={s}
                  expanded={expandedId === s.id}
                  onToggle={() => setExpandedId(expandedId === s.id ? null : s.id)}
                  onEdit={() => setEditStudent(s)}
                  onDelete={() => setDeleteId(s.id)}
                  onEnroll={() => setShowEnroll(s)}
                  onRefresh={fetchStudents}
                  fmt={fmt}
                  crmLeadLinks={crmLeadLinks}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {total > 20 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400">Showing {(page - 1) * 20 + 1} - {Math.min(page * 20, total)} of {total}</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-xs rounded-sm">Prev</Button>
            <Button variant="outline" size="sm" disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)} className="text-xs rounded-sm">Next</Button>
          </div>
        </div>
      )}

      <StudentDialog open={showAdd || !!editStudent} onClose={() => { setShowAdd(false); setEditStudent(null); }} student={editStudent} batches={batches} counsellors={counsellors} onSaved={fetchStudents} />
      <EnrollDialog open={!!showEnroll} onClose={() => setShowEnroll(null)} student={showEnroll} batches={batches} onSaved={fetchStudents} />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Student?</AlertDialogTitle><AlertDialogDescription>This will remove the student and all their enrollments.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 rounded-sm text-xs" data-testid="confirm-delete-student" onClick={() => { api.delete(`/training/students/${deleteId}`).then(() => { toast.success('Student deleted'); fetchStudents(); }).catch(() => toast.error('Failed')); setDeleteId(null); }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </BookmarkMapProvider>
  );
}

function SyncStatusBadge({ status, isRunning }) {
  if (isRunning) {
    return (
      <div className="flex items-center gap-1.5 text-[10px] text-blue-600" data-testid="sync-status-badge">
        <RefreshCw className="w-3 h-3 animate-spin" />
        <span>Syncing...</span>
      </div>
    );
  }
  if (!status) return null;
  const isSuccess = status.status === 'success';
  const time = status.completed_at || status.started_at;
  const label = time ? new Date(time).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <div className="flex items-center gap-1.5 text-[10px] text-slate-500" data-testid="sync-status-badge">
      {isSuccess ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <AlertCircle className="w-3 h-3 text-red-400" />}
      <span>Last sync: {label}</span>
      {isSuccess && status.unique_students != null && <span className="text-slate-400">({status.unique_students} students)</span>}
    </div>
  );
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${String(d).padStart(2, '0')}-${MONTHS_SHORT[m - 1]}-${y}`;
}

function ExpectedPaymentCell({ student }) {
  const earliest = student.expected_payment_date;
  const all = student.expected_payment_breakdown || [];
  if (!earliest) return <span className="text-slate-300">-</span>;
  const today = new Date();
  const dt = new Date(`${earliest}T00:00:00`);
  const days = Math.floor((dt - today) / (1000 * 60 * 60 * 24));
  const tone = days < 0 ? 'text-rose-600' : days <= 7 ? 'text-amber-600' : 'text-slate-700';
  const overdue = days < 0;
  if (all.length <= 1) {
    return (
      <span className={`font-mono text-[11px] ${tone}`} title={overdue ? `Promised ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago — overdue` : `Due in ${days} day${days === 1 ? '' : 's'}`}>
        {fmtDate(earliest)}{overdue && <span className="ml-1 text-[9px] uppercase">overdue</span>}
      </span>
    );
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={`font-mono text-[11px] underline decoration-dotted underline-offset-2 hover:opacity-80 ${tone}`} data-testid={`exp-pay-${student.id}`}>
          {fmtDate(earliest)} <span className="text-[9px] text-slate-500">+{all.length - 1} more</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Expected payment dates</p>
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-slate-500">
              <th className="text-left font-medium py-1">Invoice</th>
              <th className="text-left font-medium py-1">Expected</th>
              <th className="text-right font-medium py-1">Balance</th>
            </tr>
          </thead>
          <tbody>
            {all.map((row, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="py-1 font-mono text-[10px]">{row.invoice_number || '—'}</td>
                <td className="py-1 font-mono">{fmtDate(row.date)}</td>
                <td className="py-1 text-right tabular-nums">{(row.balance ?? 0).toLocaleString('en-IN')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PopoverContent>
    </Popover>
  );
}



function StudentRow({ student: s, expanded, onToggle, onEdit, onDelete, onEnroll, onRefresh, fmt, crmLeadLinks = {} }) {
  // crmLeadLinks[enrollment_no] === undefined → not yet checked (suppress badge)
  // crmLeadLinks[enrollment_no] === null      → checked, missing (show badge)
  // crmLeadLinks[enrollment_no] === <id>      → has lead
  const hasLeadEntry = s.enrollment_no && Object.prototype.hasOwnProperty.call(crmLeadLinks, String(s.enrollment_no));
  const missingCrm = hasLeadEntry && !crmLeadLinks[String(s.enrollment_no)];
  const hasZohoData = s.source === 'zoho' || s.invoice_count > 0;
  const noBatch = (s.enrollment_count || 0) === 0;
  const { map } = useBookmarkMap();
  const bmHighlight = bookmarkRowClass(s.enrollment_no, map);

  return (
    <>
      <TableRow className={`hover:bg-violet-50/30 cursor-pointer ${noBatch ? 'bg-amber-50/40' : ''} ${bmHighlight}`} onClick={onToggle} data-testid={`student-row-${s.id}`}>
        <TableCell className="text-center">{expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}</TableCell>
        <TableCell className="text-xs">
          <div className="flex items-center gap-1.5 flex-wrap">
            <EnrollmentNoCrmLink enrollmentNo={s.enrollment_no} hasLead={!missingCrm} />
            <MissingCrmLeadBadge missing={missingCrm} compact />
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center">
            <div className="text-sm font-medium">{s.name}</div>
            <BookmarkInline student={s} />
          </div>
          {s.email && <div className="text-[10px] text-slate-400">{s.email}</div>}
        </TableCell>
        <TableCell className="text-xs text-slate-500">{s.phone || '-'}</TableCell>
        <TableCell className="text-xs text-slate-500">{s.course || s.course_name || '-'}</TableCell>
        <TableCell className="text-xs text-right font-medium">{hasZohoData ? fmt(s.total_amount) : '-'}</TableCell>
        <TableCell className="text-xs text-right text-emerald-600">{hasZohoData ? fmt(s.paid_amount) : '-'}</TableCell>
        <TableCell className="text-xs text-right">
          {hasZohoData && s.write_off_amount > 0 ? (
            <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">
              {fmt(s.write_off_amount)}
            </Badge>
          ) : hasZohoData ? <span className="text-slate-400">-</span> : '-'}
        </TableCell>
        <TableCell className="text-xs text-right" onClick={e => e.stopPropagation()}>
          {hasZohoData && s.credit_amount > 0 ? (
            <Link
              to={`/training/credit-notes?enrollment_no=${encodeURIComponent(s.enrollment_no || '')}`}
              className="inline-flex"
              data-testid={`credit-link-${s.id}`}
              title="View credit notes"
            >
              <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100 cursor-pointer">
                {fmt(s.credit_amount)}
              </Badge>
            </Link>
          ) : hasZohoData ? <span className="text-slate-400">-</span> : '-'}
        </TableCell>
        <TableCell className="text-xs text-right text-red-500 font-medium">{hasZohoData && s.due_amount > 0 ? fmt(s.due_amount) : hasZohoData ? fmt(0) : '-'}</TableCell>
        <TableCell className="text-xs" onClick={e => e.stopPropagation()}>
          <ExpectedPaymentCell student={s} />
        </TableCell>
        <TableCell className="text-center" onClick={e => e.stopPropagation()}>
          {s.invoice_count > 0 ? (
            <InvoicePopover student={s} />
          ) : (
            <span className="text-xs text-slate-400">-</span>
          )}
        </TableCell>
        <TableCell>
          {noBatch ? (
            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 cursor-pointer" onClick={e => { e.stopPropagation(); onEnroll(); }}>
              <AlertCircle className="w-3 h-3 mr-0.5" /> Assign
            </Badge>
          ) : (
            <div className="flex flex-col gap-0.5">
              {(s.enrollments || []).slice(0, 2).map((en, i) => (
                <div key={i} className="flex items-center gap-1">
                  <span className="text-[10px] text-slate-500 truncate max-w-[100px]">{en.batch_number ? `#${en.batch_number}` : en.batch_name}</span>
                  {en.batch_status && <Badge variant="outline" className={`text-[8px] px-1 py-0 ${
                    en.batch_status === 'Running' ? 'bg-emerald-50 text-emerald-600' :
                    en.batch_status === 'Completed' ? 'bg-slate-50 text-slate-500' :
                    en.batch_status === 'On Hold' ? 'bg-amber-50 text-amber-600' :
                    'bg-blue-50 text-blue-600'
                  }`}>{en.batch_status}</Badge>}
                  {en.enrollment_type === 'upgrade' && <Badge variant="outline" className="text-[8px] px-1 py-0 bg-purple-50 text-purple-600">Upg</Badge>}
                </div>
              ))}
              {(s.enrollments || []).length > 2 && <span className="text-[9px] text-slate-400">+{s.enrollments.length - 2} more</span>}
            </div>
          )}
        </TableCell>
        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
          <div className="flex gap-1 justify-end">
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Edit" onClick={onEdit} data-testid={`edit-student-${s.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Delete" onClick={onDelete} data-testid={`delete-student-${s.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow key={`${s.id}-expand`}>
          <TableCell colSpan={12} className="bg-slate-50/50 p-4">
            <ExpandedDetails student={s} onRefresh={onRefresh} fmt={fmt} missingCrm={missingCrm} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function InvoicePopover({ student }) {
  const [invoices, setInvoices] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadInvoices = () => {
    if (invoices) return;
    setLoading(true);
    api.get(`/training/students/${student.id}/invoices`)
      .then(r => setInvoices(r.data.invoices || []))
      .catch(() => toast.error('Failed to load invoices'))
      .finally(() => setLoading(false));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
          onClick={loadInvoices}
          data-testid={`invoice-count-${student.id}`}
        >
          <FileText className="w-3 h-3" />
          {student.invoice_count}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="center">
        <div className="p-3 border-b bg-slate-50">
          <p className="text-xs font-semibold text-slate-700">Invoices for {student.name}</p>
          <p className="text-[10px] text-slate-400">{student.enrollment_no}</p>
        </div>
        {loading ? (
          <div className="p-6 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-violet-500 mx-auto" /></div>
        ) : !invoices || invoices.length === 0 ? (
          <p className="text-xs text-slate-400 p-4 text-center">No invoice details available</p>
        ) : (
          <div className="max-h-64 overflow-y-auto">
            {invoices.map((inv, i) => (
              <div key={inv.invoice_id || i} className="px-3 py-2.5 border-b last:border-0 hover:bg-slate-50/50">
                <div className="flex items-center justify-between mb-1">
                  <ZohoInvoiceLink invoiceNumber={inv.invoice_number} invoiceId={inv.invoice_id} className="text-xs" />
                  <Badge variant="outline" className={`text-[9px] ${inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : inv.status === 'partially_paid' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                    {inv.status}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-[10px] text-slate-500">
                  <span>{inv.date}</span>
                  <span>{inv.cf_program_name}</span>
                </div>
                <div className="flex items-center justify-between text-[10px] mt-1">
                  <span className="text-slate-500">Total: <span className="font-medium text-slate-700">₹{Number(inv.total).toLocaleString('en-IN')}</span></span>
                  <span className="text-slate-500">Paid: <span className="text-emerald-600">₹{Number(inv.payment_made).toLocaleString('en-IN')}</span></span>
                  {inv.write_off_amount > 0 && <span className="text-slate-500">W/O: <span className="text-red-500">₹{Number(inv.write_off_amount).toLocaleString('en-IN')}</span></span>}
                  <span className="text-slate-500">Due: <span className="text-red-500">₹{Number(inv.balance).toLocaleString('en-IN')}</span></span>
                </div>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function InvoiceDetails({ studentId, fmt }) {
  const [invoices, setInvoices] = useState(null);
  useEffect(() => {
    api.get(`/training/students/${studentId}/invoices`).then(r => setInvoices(r.data.invoices || [])).catch(() => setInvoices([]));
  }, [studentId]);

  if (!invoices || invoices.length === 0) return null;
  return (
    <div className="mt-3 p-2.5 bg-white border rounded-sm">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Invoices ({invoices.length})</p>
      <div className="space-y-1.5">
        {invoices.map((inv, i) => (
          <div key={i} className="flex items-center justify-between text-xs border-b border-dashed last:border-0 pb-1.5 last:pb-0">
            <div className="flex items-center gap-2">
              <ZohoInvoiceLink invoiceNumber={inv.invoice_number || inv.invoice_id} invoiceId={inv.invoice_id} className="text-xs" />
              <span className="text-slate-400">{inv.date || ''}</span>
              {inv.cf_program_name && <Badge variant="outline" className="text-[9px] bg-slate-50">{inv.cf_program_name}</Badge>}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-slate-500">{fmt(inv.total)}</span>
              <span className="text-emerald-600">{fmt(inv.payment_made)}</span>
              {inv.write_off_amount > 0 && <span className="text-amber-600">{fmt(inv.write_off_amount)}</span>}
              <span className={`font-medium ${inv.balance > 0 ? 'text-red-600' : 'text-slate-400'}`}>{fmt(inv.balance)}</span>
              <Badge variant="outline" className={`text-[9px] ${inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : inv.status === 'partially_paid' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{inv.status}</Badge>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExpandedDetails({ student: s, onRefresh, fmt, missingCrm = false }) {
  const [tab, setTab] = useState('overview');
  const enrollments = s.enrollments || [];

  return (
    <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="student-tabs">
      <TabsList className="flex-wrap h-auto justify-start bg-slate-100/60 border rounded-sm mb-3">
        <TabsTrigger value="overview" className="text-xs" data-testid="tab-overview">
          <User className="w-3 h-3 mr-1" />Overview
        </TabsTrigger>
        <TabsTrigger value="fees" className="text-xs" data-testid="tab-fees">
          <DollarSign className="w-3 h-3 mr-1" />Fees / Invoice
        </TabsTrigger>
        <TabsTrigger value="attendance" className="text-xs" data-testid="tab-attendance">
          <CheckCircle2 className="w-3 h-3 mr-1" />Attendance
        </TabsTrigger>
        <TabsTrigger value="pdp-mock" className="text-xs" data-testid="tab-pdp-mock">
          <Video className="w-3 h-3 mr-1" />PDP &amp; Mock
        </TabsTrigger>
        <TabsTrigger value="batch" className="text-xs" data-testid="tab-batch">
          <BookOpen className="w-3 h-3 mr-1" />Batch Info
        </TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="mt-0">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <span className="text-slate-400">Enrollment No:</span>
          <span className="flex items-center gap-2 flex-wrap">
            {missingCrm
              ? <><span className="font-medium">{s.enrollment_no || '-'}</span><MissingCrmLeadBadge missing /></>
              : <ZohoCrmLeadLink enrollmentNo={s.enrollment_no} compact />
            }
            <span className="text-slate-300">·</span>
            <ZohoCustomerLink enrollmentNo="View in Books" zohoCustomerId={s.zoho_customer_id} className="text-xs" />
          </span>
          <span className="text-slate-400">Name:</span><span className="font-medium">{s.name || '-'}</span>
          <span className="text-slate-400">Phone:</span><span>{s.phone || '-'}</span>
          <span className="text-slate-400">Email:</span><span>{s.email || '-'}</span>
          <span className="text-slate-400">Course:</span><span>{s.course || '-'}</span>
          <span className="text-slate-400">Branch:</span><span>{s.branch || '-'}</span>
          <span className="text-slate-400">Counsellor:</span><span>{s.salesperson || s.counsellor_name || '-'}</span>
          {s.source === 'zoho' && <><span className="text-slate-400">Source:</span><Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-600 border-blue-200 w-fit">Zoho Books</Badge></>}
        </div>
        {(s.total_amount != null && s.total_amount > 0) && (
          <div className="mt-3 p-2.5 bg-white border rounded-sm">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Financial Summary</p>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div><p className="text-xs text-slate-500">Total</p><p className="text-sm font-semibold">{fmt(s.total_amount)}</p></div>
              <div><p className="text-xs text-slate-500">Paid</p><p className="text-sm font-semibold text-emerald-600">{fmt(s.paid_amount)}</p></div>
              <div><p className="text-xs text-slate-500">Write Off</p><p className={`text-sm font-semibold ${s.write_off_amount > 0 ? 'text-red-500' : 'text-slate-400'}`}>{s.write_off_amount > 0 ? fmt(s.write_off_amount) : '-'}</p></div>
              <div><p className="text-xs text-slate-500">Due</p><p className="text-sm font-semibold text-red-500">{fmt(s.due_amount)}</p></div>
            </div>
          </div>
        )}
      </TabsContent>

      <TabsContent value="fees" className="mt-0">
        <InvoiceDetails studentId={s.id} fmt={fmt} />
      </TabsContent>

      <TabsContent value="attendance" className="mt-0">
        <AttendanceTab studentId={s.id} />
      </TabsContent>

      <TabsContent value="pdp-mock" className="mt-0">
        <PdpMockTab studentId={s.id} />
      </TabsContent>

      <TabsContent value="batch" className="mt-0">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Enrollments ({enrollments.length})</p>
        {enrollments.length === 0 ? (
          <p className="text-xs text-slate-400">No enrollments yet. Click the book icon to enroll in a batch.</p>
        ) : (
          <div className="grid gap-2">
            {enrollments.map(e => (
              <div key={e.id} className="flex items-center gap-4 border rounded-sm p-3 bg-white text-xs">
                <div className="flex flex-col">
                  <span className="font-medium text-slate-600">{e.batch_name || e.course_name || 'Unknown'}</span>
                  {e.batch_number && <span className="text-[10px] text-slate-400">Batch #{e.batch_number}</span>}
                </div>
                {e.batch_status && (
                  <Badge variant="outline" className={`text-[9px] ${
                    e.batch_status === 'Running' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    e.batch_status === 'Upcoming' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                    e.batch_status === 'On Hold' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    e.batch_status === 'Completed' ? 'bg-slate-100 text-slate-600 border-slate-200' :
                    'bg-slate-100 text-slate-500'
                  }`}>{e.batch_status}</Badge>
                )}
                {e.enrollment_type === 'upgrade' && (
                  <Badge variant="outline" className="text-[9px] bg-purple-50 text-purple-700 border-purple-200">Upgrade</Badge>
                )}
                {e.zoho_batch_id && (
                  <ZohoCrmBatchLink zohoBatchId={e.zoho_batch_id} compact />
                )}
                <div className="flex-1" />
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                  if (!window.confirm('Remove this enrollment?')) return;
                  api.delete(`/training/enrollments/${e.id}`).then(() => { toast.success('Enrollment removed'); onRefresh(); }).catch(() => toast.error('Failed'));
                }} data-testid={`delete-enrollment-${e.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
              </div>
            ))}
          </div>
        )}
      </TabsContent>

      {/* Centralised activity timeline — aggregates comments from every module */}
      <div className="mt-3 pt-3 border-t border-slate-200">
        <StudentTimeline studentId={s.id} />
      </div>
    </Tabs>
  );
}

// ── Attendance tab ─────────────────────────────────────────
function AttendanceTab({ studentId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(`/training/students/${studentId}/attendance`).then(r => { if (live) setRows(r.data.attendance || []); }).catch(() => { if (live) setRows([]); });
    return () => { live = false; };
  }, [studentId]);
  if (rows === null) return <p className="text-xs text-slate-400">Loading attendance…</p>;
  if (rows.length === 0) return <p className="text-xs text-slate-400 py-6 text-center">No attendance records found.</p>;
  const counts = rows.reduce((acc, r) => { const s = (r.status || '').toLowerCase(); acc[s] = (acc[s] || 0) + 1; return acc; }, {});
  return (
    <div data-testid="attendance-panel">
      <div className="flex flex-wrap gap-2 mb-3 text-[10px] uppercase font-semibold">
        {Object.entries(counts).map(([k, v]) => (
          <span key={k} className={`px-2 py-1 rounded ${
            k === 'present' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
            k === 'absent' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
            'bg-slate-100 text-slate-600 border border-slate-200'
          }`}>{k || 'unknown'}: {v}</span>
        ))}
        <span className="px-2 py-1 rounded bg-slate-50 text-slate-500 border border-slate-200">Total: {rows.length}</span>
      </div>
      <div className="max-h-64 overflow-y-auto border rounded-sm bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="text-left px-3 py-1.5 text-[10px] uppercase text-slate-400 font-medium">Date</th>
              <th className="text-left px-3 py-1.5 text-[10px] uppercase text-slate-400 font-medium">Status</th>
              <th className="text-left px-3 py-1.5 text-[10px] uppercase text-slate-400 font-medium">Batch</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t hover:bg-slate-50">
                <td className="px-3 py-1.5 tabular-nums text-slate-700">{r.date}</td>
                <td className="px-3 py-1.5">
                  <Badge variant="outline" className={`text-[9px] ${
                    (r.status || '').toLowerCase() === 'present' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    (r.status || '').toLowerCase() === 'absent' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                    'bg-slate-100 text-slate-600 border-slate-200'
                  }`}>{r.status || '—'}</Badge>
                </td>
                <td className="px-3 py-1.5 text-slate-500">{r.batch_name || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── PDP & Mock Interview tab ───────────────────────────────
function PdpMockTab({ studentId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(`/training/students/${studentId}/pdp-mock`).then(r => { if (live) setRows(r.data.batches || []); }).catch(() => { if (live) setRows([]); });
    return () => { live = false; };
  }, [studentId]);
  if (rows === null) return <p className="text-xs text-slate-400">Loading PDP &amp; mock interview…</p>;
  if (rows.length === 0) return <p className="text-xs text-slate-400 py-6 text-center">No batch enrollments for this student.</p>;
  const missingCount = rows.filter(b => !b.mock_interview_link).length;
  return (
    <div data-testid="pdp-mock-panel" className="space-y-2">
      {missingCount > 0 && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5" />
          {missingCount} of {rows.length} batch{rows.length > 1 ? 'es' : ''} still missing a mock interview link.
        </p>
      )}
      {rows.map((b) => (
        <div key={b.batch_id} className="border bg-white rounded-sm p-3 text-xs space-y-2" data-testid={`pdp-mock-row-${b.batch_id}`}>
          <div className="flex items-center flex-wrap gap-2">            <span className="font-medium text-slate-700">{b.batch_name || 'Unknown batch'}</span>
            {b.batch_number && <span className="text-[10px] text-slate-400">#{b.batch_number}</span>}
            {b.batch_status && (
              <Badge variant="outline" className={`text-[9px] ${
                b.batch_status === 'Running' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                b.batch_status === 'Completed' ? 'bg-slate-100 text-slate-600 border-slate-200' :
                'bg-blue-50 text-blue-700 border-blue-200'
              }`}>{b.batch_status}</Badge>
            )}
            <span className="text-[10px] text-slate-400">Trainer: {b.trainer_name || '—'}</span>
            {b.updated_at && <span className="text-[10px] text-slate-300 ml-auto">Updated {new Date(b.updated_at).toLocaleDateString()} · {b.updated_by || '—'}</span>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="border border-slate-100 rounded-sm p-2 bg-slate-50/40">
              <p className="text-[9px] uppercase text-slate-400 font-semibold mb-0.5">PDP</p>
              <p className="text-slate-700">{b.pdp || <span className="text-slate-300 italic">Not set</span>}</p>
            </div>
            <div className="border border-slate-100 rounded-sm p-2 bg-slate-50/40">
              <p className="text-[9px] uppercase text-slate-400 font-semibold mb-0.5">Mock Interview</p>
              <p className="text-slate-700">{b.mock_interview || <span className="text-slate-300 italic">Not set</span>}</p>
            </div>
            <div className="border border-slate-100 rounded-sm p-2 bg-slate-50/40">
              <p className="text-[9px] uppercase text-slate-400 font-semibold mb-0.5 flex items-center gap-1"><Video className="w-2.5 h-2.5" />Video / Transcript</p>
              {b.mock_interview_link ? (
                <a href={b.mock_interview_link} target="_blank" rel="noopener noreferrer"
                  className="text-violet-600 hover:underline inline-flex items-center gap-1 truncate max-w-full"
                  data-testid={`pdp-mock-link-${b.batch_id}`}>
                  <Link2 className="w-3 h-3" />Open link
                </a>
              ) : (
                <span className="text-rose-500 italic text-[11px]">Missing</span>
              )}
            </div>
          </div>
          {/* Admin/manager inline edit */}
          <div className="mt-2 pt-2 border-t border-slate-100">
            <PdpMockEditor
              batchId={b.batch_id}
              studentId={studentId}
              initial={{ pdp: b.pdp, mock_interview: b.mock_interview, mock_interview_link: b.mock_interview_link }}
              endpoint="admin"
              onSaved={(next) => {
                setRows((prev) => prev.map((r) => r.batch_id === b.batch_id ? { ...r, ...next, updated_at: new Date().toISOString() } : r));
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function StudentDialog({ open, onClose, student, batches, counsellors, onSaved }) {
  const [form, setForm] = useState({ name: '', enrollment_no: '', phone: '', email: '', counsellor_id: '', batch_id: '', remarks: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (student) {
        setForm({
          name: student.name || '', enrollment_no: student.enrollment_no || '',
          phone: student.phone || '', email: student.email || '',
          counsellor_id: student.counsellor_id || '', batch_id: '',
          remarks: student.remarks || '',
        });
      } else {
        setForm({ name: '', enrollment_no: '', phone: '', email: '', counsellor_id: '', batch_id: '', remarks: '' });
      }
    }
  }, [open, student]);

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      if (student) {
        await api.put(`/training/students/${student.id}`, {
          name: form.name, enrollment_no: form.enrollment_no,
          phone: form.phone, email: form.email,
          counsellor_id: form.counsellor_id, remarks: form.remarks,
        });
      } else {
        await api.post('/training/students', form);
      }
      toast.success(student ? 'Student updated' : 'Student created');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="student-dialog">
        <DialogHeader><DialogTitle className="text-base">{student ? 'Edit Student' : 'Add Student'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Enrollment No</Label><Input value={form.enrollment_no} onChange={e => setForm(p => ({ ...p, enrollment_no: e.target.value }))} className="h-8 text-xs" data-testid="student-enrollment-no" placeholder="From invoice" /></div>
            <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="h-8 text-xs" data-testid="student-name-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="h-8 text-xs" data-testid="student-phone-input" /></div>
            <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="h-8 text-xs" data-testid="student-email-input" /></div>
          </div>
          <div>
            <Label className="text-xs">Counsellor</Label>
            <Select value={form.counsellor_id || 'none'} onValueChange={v => setForm(p => ({ ...p, counsellor_id: v === 'none' ? '' : v }))}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem>{counsellors.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Remarks</Label>
            <Input value={form.remarks} onChange={e => setForm(p => ({ ...p, remarks: e.target.value }))} className="h-8 text-xs" data-testid="student-remarks-input" placeholder="Optional notes" />
          </div>
          {!student && (
            <>
              <hr className="border-border" />
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Initial Enrollment (Optional)</p>
              <div>
                <Label className="text-xs">Batch</Label>
                <Select value={form.batch_id || 'none'} onValueChange={v => setForm(p => ({ ...p, batch_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select batch" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">No batch</SelectItem>{batches.map(b => <SelectItem key={b.id} value={b.id}>{b.course_name} - {b.start_date}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-violet-600 hover:bg-violet-700 rounded-sm text-xs" onClick={handleSave} disabled={saving} data-testid="save-student-btn">{saving ? 'Saving...' : 'Save'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EnrollDialog({ open, onClose, student, batches, onSaved }) {
  const [batchId, setBatchId] = useState('');
  const [saving, setSaving] = useState(false);
  const [batchSearch, setBatchSearch] = useState('');

  useEffect(() => { if (open) { setBatchId(''); setBatchSearch(''); } }, [open]);

  const filteredBatches = batches.filter(b => {
    if (!batchSearch) return true;
    const s = batchSearch.toLowerCase();
    const bn = b.batch_number ? `#${b.batch_number}` : '';
    return (b.course_name || '').toLowerCase().includes(s) ||
      (b.name || '').toLowerCase().includes(s) ||
      bn.toLowerCase().includes(s) ||
      (b.start_date || '').includes(s);
  });

  const handleSave = async () => {
    if (!batchId) return toast.error('Select a batch');
    setSaving(true);
    try {
      await api.post('/training/enrollments', { student_id: student.id, batch_id: batchId });
      toast.success('Student enrolled');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const selectedBatch = batches.find(b => b.id === batchId);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="enroll-dialog">
        <DialogHeader><DialogTitle className="text-base">Enroll {student?.name} in Batch</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Search Batch</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                placeholder="Search by batch #, course, name..."
                value={batchSearch}
                onChange={e => setBatchSearch(e.target.value)}
                className="h-8 text-xs pl-8"
                data-testid="enroll-batch-search"
              />
            </div>
          </div>
          {selectedBatch && (
            <div className="flex items-center gap-2 p-2 bg-violet-50 border border-violet-200 rounded-sm">
              <span className="text-xs font-semibold text-violet-700">#{selectedBatch.batch_number}</span>
              <span className="text-xs text-slate-600">{selectedBatch.course_name}</span>
              <span className="text-[10px] text-slate-400">{selectedBatch.start_date}</span>
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0 ml-auto" onClick={() => setBatchId('')}>
                <X className="w-3 h-3 text-slate-400" />
              </Button>
            </div>
          )}
          <div className="max-h-48 overflow-y-auto border rounded-sm">
            {filteredBatches.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">No batches found</p>
            ) : filteredBatches.map(b => (
              <div
                key={b.id}
                className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-violet-50 border-b last:border-0 text-xs ${batchId === b.id ? 'bg-violet-50 border-l-2 border-l-violet-500' : ''}`}
                onClick={() => setBatchId(b.id)}
                data-testid={`enroll-batch-option-${b.id}`}
              >
                <span className="font-semibold text-violet-600 w-12">#{b.batch_number || '-'}</span>
                <span className="font-medium text-slate-700 flex-1 truncate">{b.course_name || b.name}</span>
                {b.batch_status && (
                  <Badge variant="outline" className={`text-[8px] px-1 py-0 ${
                    b.batch_status === 'Running' ? 'bg-emerald-50 text-emerald-600' :
                    b.batch_status === 'Upcoming' ? 'bg-blue-50 text-blue-600' :
                    b.batch_status === 'On Hold' ? 'bg-amber-50 text-amber-600' :
                    'bg-slate-50 text-slate-500'
                  }`}>{b.batch_status}</Badge>
                )}
                <span className="text-[10px] text-slate-400">{b.start_date || ''}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-violet-600 hover:bg-violet-700 rounded-sm text-xs" onClick={handleSave} disabled={saving || !batchId} data-testid="enroll-save-btn">{saving ? 'Enrolling...' : 'Enroll'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
